/**
 * Thin fetch wrapper for the ADR-M24 backend API.
 *
 * - Always relative to /api/v1/ — nginx proxies it to the api container.
 * - Injects the Authorization: Bearer header from the auth store when
 *   present. The store decides whether the token is valid.
 * - Throws ApiError with the RFC 7807 `code` so callers can branch on
 *   stable machine-readable strings rather than i18n titles.
 */

import { useAuthStore } from '../stores/auth';

const API_BASE = '/api/v1';

export type ProblemDetails = {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  code: string;
  retry_after_seconds?: number;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  withAuth?: boolean;
  signal?: AbortSignal;
};

// Promesse de refresh en cours, partagée entre toutes les requêtes
// concurrentes pour éviter de lancer N appels /auth/refresh en parallèle.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return false;
  try {
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!resp.ok) {
      // Refresh token expiré/révoqué → on déconnecte.
      useAuthStore.getState().clear();
      return false;
    }
    const data = (await resp.json()) as { access_token: string };
    useAuthStore.getState().setAccessToken(data.access_token);
    return true;
  } catch {
    return false;
  }
}

async function request<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const { method = 'GET', body, formData, withAuth = true, signal } = opts;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (withAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const init: RequestInit = { method, headers };
  if (signal !== undefined) {
    init.signal = signal;
  }
  if (formData !== undefined) {
    init.body = formData;
    // Let the browser set Content-Type with multipart boundary
    delete headers['Content-Type'];
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const resp = await fetch(`${API_BASE}${path}`, init);

  if (resp.status === 204) {
    return undefined as T;
  }
  // 202 = job lancé en arrière-plan, le body contient {status: 'queued', ...}
  // qu'il faut renvoyer au caller (gère le toast async).

  // 401 + refresh token disponible + pas déjà retried → refresh + retry une fois.
  if (
    resp.status === 401 &&
    withAuth &&
    !retried &&
    !path.startsWith('/auth/') &&
    useAuthStore.getState().refreshToken
  ) {
    refreshInFlight = refreshInFlight ?? refreshAccessToken();
    const ok = await refreshInFlight;
    refreshInFlight = null;
    if (ok) {
      return request<T>(path, opts, true);
    }
  }

  const text = await resp.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { raw: text };
    }
  }

  if (!resp.ok) {
    if (parsed && typeof parsed === 'object' && 'code' in parsed && 'status' in parsed) {
      throw new ApiError(parsed as ProblemDetails);
    }
    throw new ApiError({
      title: `HTTP ${resp.status}`,
      status: resp.status,
      code: 'ADR-M24:unknown',
      detail: text || resp.statusText,
    });
  }

  return parsed as T;
}

type DownloadBlobOptions = {
  method?: 'GET' | 'POST';
  body?: unknown;
};

async function downloadBlob(
  path: string,
  suggestedFilename: string,
  opts: DownloadBlobOptions = {}
): Promise<void> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  const token = useAuthStore.getState().accessToken;
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const init: RequestInit = { method, headers };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  let resp = await fetch(`${API_BASE}${path}`, init);

  if (resp.status === 401 && useAuthStore.getState().refreshToken) {
    refreshInFlight = refreshInFlight ?? refreshAccessToken();
    const ok = await refreshInFlight;
    refreshInFlight = null;
    if (ok) {
      const t2 = useAuthStore.getState().accessToken;
      if (t2) headers['Authorization'] = `Bearer ${t2}`;
      resp = await fetch(`${API_BASE}${path}`, init);
    }
  }

  if (!resp.ok) {
    throw new ApiError({
      title: `HTTP ${resp.status}`,
      status: resp.status,
      code: 'ADR-M24:download_failed',
      detail: resp.statusText,
    });
  }

  const cd = resp.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const filename = (match && match[1]) || suggestedFilename;

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Variante de downloadBlob qui retourne le blob (+ filename) sans déclencher
 * de téléchargement browser. Utilisé pour réuploader vers Drive depuis le
 * composant SendExportToDriveBtn.
 */
async function fetchBlob(
  path: string,
  opts: DownloadBlobOptions = {}
): Promise<{ blob: Blob; filename: string | null; contentType: string }> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  const token = useAuthStore.getState().accessToken;
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const init: RequestInit = { method, headers };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  let resp = await fetch(`${API_BASE}${path}`, init);

  if (resp.status === 401 && useAuthStore.getState().refreshToken) {
    refreshInFlight = refreshInFlight ?? refreshAccessToken();
    const ok = await refreshInFlight;
    refreshInFlight = null;
    if (ok) {
      const t2 = useAuthStore.getState().accessToken;
      if (t2) headers['Authorization'] = `Bearer ${t2}`;
      resp = await fetch(`${API_BASE}${path}`, init);
    }
  }

  if (!resp.ok) {
    throw new ApiError({
      title: `HTTP ${resp.status}`,
      status: resp.status,
      code: 'ADR-M24:fetch_blob_failed',
      detail: resp.statusText,
    });
  }

  const cd = resp.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const filename = (match && match[1]) || null;
  const contentType = resp.headers.get('Content-Type') || 'application/octet-stream';
  const blob = await resp.blob();
  return { blob, filename, contentType };
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  postForm: <T>(
    path: string,
    formData: FormData,
    opts?: Omit<RequestOptions, 'method' | 'body' | 'formData'>
  ) => request<T>(path, { ...opts, method: 'POST', formData }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
  downloadBlob,
  fetchBlob,
};
